import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";

// Scope is derived from the URL path prefix, not state. The sidebar's
// Notice AI / Partner Admin sub-menus link to `/admin/notice-ai/*` and
// `/admin/partner/*` respectively; everything else under `/admin/*` is the
// legacy "global" view that maps to `scope=all`. Keeping derivation read-only
// means there's no risk of state and URL drifting apart on reload or back-nav.
export const ADMIN_SCOPE_DIRECT = "direct";
export const ADMIN_SCOPE_PARTNER = "partner";
export const ADMIN_SCOPE_ALL = "all";

const AdminScopeContext = createContext({
  scope: ADMIN_SCOPE_ALL,
  pathPrefix: "/admin",
});

// Derives the scope from a pathname. Exported so route guards / fetch helpers
// outside React can compute scope from a known URL without spinning up a
// component. Pure function, no router dependency.
export const deriveAdminScope = (pathname) => {
  if (typeof pathname !== "string") return ADMIN_SCOPE_ALL;
  if (pathname === "/admin/notice-ai" || pathname.startsWith("/admin/notice-ai/")) {
    return ADMIN_SCOPE_DIRECT;
  }
  if (pathname === "/admin/partner" || pathname.startsWith("/admin/partner/")) {
    return ADMIN_SCOPE_PARTNER;
  }
  return ADMIN_SCOPE_ALL;
};

// Maps a scope back to its sidebar path prefix. Useful when an admin page
// wants to navigate to a sibling page within the same scope without having
// to know its own URL: navigate(`${pathPrefix}/users`) keeps the scope intact.
export const scopeToPathPrefix = (scope) => {
  if (scope === ADMIN_SCOPE_DIRECT) return "/admin/notice-ai";
  if (scope === ADMIN_SCOPE_PARTNER) return "/admin/partner";
  return "/admin";
};

export const AdminScopeProvider = ({ children }) => {
  const { pathname } = useLocation();
  const value = useMemo(() => {
    const scope = deriveAdminScope(pathname);
    return { scope, pathPrefix: scopeToPathPrefix(scope) };
  }, [pathname]);
  return (
    <AdminScopeContext.Provider value={value}>
      {children}
    </AdminScopeContext.Provider>
  );
};

export const useAdminScope = () => useContext(AdminScopeContext);
